import React, { useState, useEffect, useRef } from "react";
import classnames from "classnames";
import _throttle from "lodash/throttle";
import browser from "web/script/utils/browser";
import analytics from "web/script/analytics/analytics";
import userProfiler from "web/script/modules/userprofiler";
import globalEvents from "web/script/utils/global-events";
import { gettext } from "web/script/modules/django-i18n";
import { CartSummary as CartSummarySerializer } from "web/types/serializers";
import TrackedButton from "web/react/components/tracked-button/tracked-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { useRothko, UseRothkoLoadingStates } from "web/react/hooks/use-rothko/use-rothko";
import CartSummaryLastAdded from "./cart-summary-last-added";
import CartSummaryOOS from "./cart-summary-oos";

// eslint-disable-next-line css-modules/no-unused-class
import styles from "./cart-summary.css";

export const CART_SUMMARY_API_ENDPOINT = "modules/cart_summary";

export function CartSummaryLoading(): JSX.Element {
    return (
        <div className={styles.loading}>
            <LoadingSpinner type="highlight" />
        </div>
    );
}

export function CartSummaryEmpty(): JSX.Element {
    return (
        <div className={styles.empty}>
            <p className="mt0">{"Your bag is empty"}</p>
        </div>
    );
}

export function CartSummary({
    cartUrl,
    itemsCount,
    wrapperClassName,
}: {
    cartUrl: string;
    itemsCount: number;
    wrapperClassName?: string;
}): JSX.Element {
    const cartSummaryRef = useRef<HTMLDivElement>(null);
    const { data, loadingState, run } = useRothko<CartSummarySerializer>(
        CART_SUMMARY_API_ENDPOINT,
        undefined,
        {
            enabled: false,
            rothko: {
                cache: false,
            },
        }
    );
    const [persist, setPersist] = useState(false);
    const [forceHide, setForceHide] = useState(false);

    function renderContent(): JSX.Element {
        switch (data?.state) {
            case "empty":
                return <CartSummaryEmpty />;
            case "last_added":
                return (
                    <CartSummaryLastAdded
                        checkoutUrl={data?.checkout_url}
                        item={data?.item}
                        promotions={data?.item_promotions}
                        itemCount={data?.item_count}
                        totalPrice={data?.total_price_with_currency_symbol}
                        productAnalyticsData={data?.product_analytics_data}
                    />
                );
            case "oos_notification":
                return (
                    <CartSummaryOOS
                        id={data?.item!.product_id}
                        image={data?.item!.image}
                        designer={data?.item!.designer}
                        size={data?.item!.size}
                        isLoggedIn={data?.save_for_later?.is_user_authenticated}
                    />
                );
            default:
                return <CartSummaryLoading />;
        }
    }

    const onMouseEnter = _throttle(() => {
        run?.();
        setForceHide(false);
        analytics.event("header", "show-cart-dropdown");
    }, 300);

    function handleClose(): void {
        setPersist(false);
        setForceHide(true);
        document
            ?.querySelector(".lyst-app__layout-container--dimmed")
            ?.classList.remove("lyst-app__layout-container--dimmed");
        document?.querySelector("body")?.classList.remove("no-scroll");
        analytics.event("header", "hide-cart-callout");
    }

    useEffect(() => {
        if (data && data.state == "oos_notification") {
            if (data.item && !userProfiler.hasSeenOOSNotification(data.item.product_id)) {
                setPersist(true);
                globalEvents.trigger("oosItemFound");
                analytics.event("header", "show-cart-callout-oos", undefined, true);
                userProfiler.saveSeenOOSNotification(data.item.product_id);
            } else {
                setPersist(false);
            }
        }
    }, [data]);

    // Listen for when the user adds something to the bag so we can update the cart summary, show it and scroll to top
    useEffect(() => {
        function handleAddedToBag(): void {
            const hasOverlayOpen = document.querySelector(".base-overlay");

            run?.();
            setPersist(true);
            setForceHide(false);

            analytics.event("header", "show-cart-callout");

            // Wait arbitrary time for the overlay to close if we are coming from an overlay
            if (hasOverlayOpen) {
                setTimeout(() => {
                    browser.scrollTop(0);
                }, 600);
            } else {
                browser.scrollTop(0);
            }
        }

        globalEvents.on("added-to-bag", handleAddedToBag);

        return (): void => {
            globalEvents.off("added-to-bag", handleAddedToBag);
        };
    }, [run, wrapperClassName]);

    // Remove the persisted cart summary if clicked outsite
    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (!cartSummaryRef?.current?.contains?.(event.target as Node) && persist) {
                handleClose();
            }
        }

        document.addEventListener("mousedown", handleClickOutside);

        return (): void => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [cartSummaryRef, persist]);

    return (
        <div
            className={classnames(styles.outerWrapper, wrapperClassName, {
                "hidden-mobile": itemsCount === 0 && (!data || data.item_count === 0) && !persist,
            })}
        >
            <div className={classnames(styles.wrapper, styles.glyph)}>
                <div onMouseEnter={onMouseEnter}>
                    <TrackedButton
                        className={styles.icon}
                        href={cartUrl}
                        eventCategory="nav"
                        eventAction="clicked"
                        eventLabel="user_click_bag"
                    >
                        <div className={styles.iconLink}>
                            <SVGIcon className={styles.shoppingBagIcon} name="shoppingbag-icon" />

                            {data?.item_count || itemsCount ? (
                                <div className={styles.cartNumber} data-test-id="cart-number">
                                    {data?.item_count || itemsCount}
                                </div>
                            ) : null}
                        </div>

                        <div className={classnames(styles.iconText, "hidden-tablet")}>
                            {gettext("header.basket")}
                        </div>

                        <span
                            className={classnames(styles.triangle, {
                                [styles.persist]: persist,
                                [styles.hide]: forceHide,
                            })}
                        ></span>
                    </TrackedButton>
                </div>

                <div
                    ref={cartSummaryRef}
                    className={classnames(styles.cartSummary, {
                        [styles.persist]: persist,
                        [styles.hide]: forceHide,
                    })}
                >
                    <div className={styles.cartSummaryInner}>
                        {loadingState === UseRothkoLoadingStates.PENDING ? (
                            <CartSummaryLoading />
                        ) : (
                            renderContent()
                        )}

                        {persist ? (
                            <div
                                className={styles.closeButton}
                                onClick={handleClose}
                                data-test-id="close-button"
                            >
                                <SVGIcon className={styles.crossIcon} name="cross" />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

export function CartSummaryWrapper({
    cartUrl,
    itemsCount,
    wrapperClassName,
}: {
    cartUrl: string;
    itemsCount: number;
    wrapperClassName?: string;
}): JSX.Element | null {
    return (
        <CartSummary
            cartUrl={cartUrl}
            itemsCount={itemsCount}
            wrapperClassName={wrapperClassName}
        />
    );
}

export default CartSummaryWrapper;
